/**
 * Comments Poster - Handles submitting comments to WordPress REST API
 * 
 * This module provides functionality to post comments from the frontend
 * to a WordPress REST API endpoint.
 */

/**
 * Submit a comment to the WordPress REST API
 * 
 * @param {number} postId - The ID of the post to comment on
 * @param {string} content - The comment content
 * @param {Object} options - Optional configuration
 * @param {string} options.endpoint - Custom endpoint (default: 'authbridge/v1/submit-comment')
 * @param {Function} options.onSuccess - Callback for successful submission
 * @param {Function} options.onError - Callback for error handling
 * @returns {Promise} - The fetch promise
 */
export const submitComment = async (postId, content, options = {}) => {
  
  if (!postId || !content) {
    const error = new Error('Post ID and comment content are required');
    if (options.onError) {
      options.onError(error);
    }
    return Promise.reject(error);
  }

  const endpoint = options.endpoint || 'authbridge/v1/submit-comment';
  const url = `${pocket.siteurl}/wp-json/${endpoint}`;
  
  try {
    // Try to get the nonce from the page if available
    let nonce = '';
    if (typeof window !== 'undefined') {
      // Try to find the nonce in different possible locations
      if (window.wpApiSettings?.nonce) {
        nonce = window.wpApiSettings.nonce;
      } else if (document.querySelector('meta[name="wp-rest-nonce"]')?.getAttribute('content')) {
        nonce = document.querySelector('meta[name="wp-rest-nonce"]').getAttribute('content');
      }
    }
    
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin', // Include cookies in the request
      headers: {
        'Content-Type': 'application/json',
        ...(nonce ? { 'X-WP-Nonce': nonce } : {}),
      },
      body: JSON.stringify({
        post_id: postId,
        content,
        parent_id: options.parentId || 0,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (options.onSuccess) {
      options.onSuccess(data);
    }
    
    return data;
  } catch (error) {
    if (options.onError) {
      options.onError(error);
    }
    throw error;
  }
};

/**
 * Check if a comment text is valid
 * 
 * @param {string} content - The comment content to validate
 * @returns {boolean} - Whether the comment is valid
 */
export const validateComment = (content) => {
  if (!content || typeof content !== 'string') {
    return false;
  }
  
  // Trim and check if content is empty
  const trimmed = content.trim();
  if (trimmed.length === 0) {
    return false;
  }
  
  // Additional validation rules could be added here
  
  return true;
};

/**
 * Create a comment form handler
 * 
 * @param {Object} config - Configuration object
 * @param {string|HTMLElement} config.formSelector - The comment form selector or element
 * @param {string|HTMLElement} config.textareaSelector - The comment textarea selector or element
 * @param {string|HTMLElement} config.submitButtonSelector - The submit button selector or element
 * @param {number} config.postId - The post ID to comment on
 * @param {Function} config.onSuccess - Callback for successful submission
 * @param {Function} config.onError - Callback for error handling
 * @returns {Object} - Form handler methods
 */
export const createCommentFormHandler = (config) => {
  const form = typeof config.formSelector === 'string' 
    ? document.querySelector(config.formSelector) 
    : config.formSelector;
    
  const textarea = typeof config.textareaSelector === 'string'
    ? document.querySelector(config.textareaSelector)
    : config.textareaSelector;
    
  const submitButton = typeof config.submitButtonSelector === 'string'
    ? document.querySelector(config.submitButtonSelector)
    : config.submitButtonSelector;
  
  if (!form || !textarea || !submitButton || !config.postId) {
    console.error('Comment form: Missing required elements or post ID');
    return null;
  }
  
  const postId = config.postId;
  
  // Set initial state
  let isSubmitting = false;
  
  /**
   * Handle form submission
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (isSubmitting) {
      return;
    }
    
    const content = textarea.value;
    
    if (!validateComment(content)) {
      if (config.onError) {
        config.onError(new Error('Please enter a valid comment'));
      }
      return;
    }
    
    isSubmitting = true;
    submitButton.disabled = true;
    
    try {
      const result = await submitComment(postId, content, {
        onSuccess: config.onSuccess,
        onError: config.onError,
      });
      
      // Clear the textarea on success
      textarea.value = '';
      
      return result;
    } finally {
      isSubmitting = false;
      submitButton.disabled = false;
    }
  };
  
  // Attach event listener
  form.addEventListener('submit', handleSubmit);
  
  return {
    destroy: () => {
      form.removeEventListener('submit', handleSubmit);
    },
    getFormElement: () => form,
    submit: handleSubmit,
  };
};

export default {
  submitComment,
  validateComment,
  createCommentFormHandler,
};